import { Canvas } from '@react-three/fiber';
import { TeethViewer } from '@patientMenu/patientDetail/patientScan/TeethViewer.tsx';
import { TeethViewerOptions } from '@patientMenu/patientDetail/patientScan/TeethViewerOptions.tsx';
import { useThreeModel } from '@hooks/useThreeModel.ts';
import { usePatientDetailStore } from '@patientMenu/hooks/usePatientDetailStore';
import { cn } from '@/lib/utils';

interface TeethPreviewProps {
  className?: string;
}

export default function TeethPreview({ className }: TeethPreviewProps) {
  const { selectedScan } = usePatientDetailStore();

  if (!selectedScan) {
    return null;
  }

  const lowerScanQuery = useThreeModel(selectedScan.lowerJaw.model3dUrl);
  const upperScanQuery = useThreeModel(selectedScan.upperJaw.model3dUrl);

  const baseCardClass =
    'flex h-full min-h-0 w-full flex-col rounded-3xl border border-content-highlight/35 bg-content-dark/45 p-4 text-white shadow-inner shadow-primary/10 backdrop-blur lg:p-5';
  const baseBodyClass = 'flex min-h-[14rem] flex-1 items-center justify-center';

  if (lowerScanQuery.isLoading || upperScanQuery.isLoading) {
    return (
      <section className={cn(baseCardClass, baseBodyClass, className)}>
        <p className="text-white/70">Loading scan preview…</p>
      </section>
    );
  }

  if (lowerScanQuery.isError || upperScanQuery.isError || !lowerScanQuery.data || !upperScanQuery.data) {
    return (
      <section className={cn(baseCardClass, baseBodyClass, className)}>
        <p className="text-white/70">Unable to load 3D models.</p>
      </section>
    );
  }

  return (
    <section className={cn('flex h-full min-h-0 w-full flex-col gap-3', baseCardClass, className)}>
      <header className="shrink-0">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/60">Preview</p>
      </header>
      <div className="relative min-h-[14rem] flex-1 w-full overflow-hidden rounded-2xl border border-content-highlight/30 bg-black/40">
        <Canvas camera={{ position: [0, 0, 120], fov: 35, near: 0.1, far: 500 }}>
          <TeethViewer upperJaw={upperScanQuery.data} lowerJaw={lowerScanQuery.data} />
        </Canvas>
        <div className="absolute left-4 bottom-4 z-10">
          <div className="rounded-2xl border border-content-highlight/40 bg-content/85 px-4 py-2 shadow-lg shadow-content-dark/40">
            <TeethViewerOptions />
          </div>
        </div>
      </div>
    </section>
  );
}
